const comparisonOperators = [
  "eq",
  "ne",
  "gt",
  "gte",
  "lt",
  "lte",
  "isnull",
  "isnotnull",
  "like",
  "in",
  "nin",
] as const;
type ComparisonOperators = (typeof comparisonOperators)[number];

export function isComparisonOperator(
  operator: string
): operator is ComparisonOperators {
  return comparisonOperators.includes(operator as ComparisonOperators);
}

type FieldFilterValue = string | number | boolean | Date | string[] | number[];

interface BaseFieldFilter {
  operator: ComparisonOperators;
  value: FieldFilterValue;
}

interface EqualFilter extends BaseFieldFilter {
  operator: "eq";
  value: string | number | boolean | Date;
}

function isEqualFilter(filter: FieldFilter): filter is EqualFilter {
  return filter.operator === "eq";
}

interface NotEqualFilter extends BaseFieldFilter {
  operator: "ne";
  value: string | number | boolean | Date;
}

function isNotEqualFilter(filter: FieldFilter): filter is NotEqualFilter {
  return filter.operator === "ne";
}

interface GreaterThanFilter extends BaseFieldFilter {
  operator: "gt";
  value: string | number | boolean | Date;
}

interface GreaterThanOrEqualFilter extends BaseFieldFilter {
  operator: "gte";
  value: number;
}

interface LessThanFilter extends BaseFieldFilter {
  operator: "lt";
  value: string | number | boolean | Date;
}

interface LessThanOrEqualFilter extends BaseFieldFilter {
  operator: "lte";
  value: number;
}

type NumericOperator = "gte" | "lte";

function numericFilter(
  operator: "gte",
  value: number
): GreaterThanOrEqualFilter;

function numericFilter(operator: "lte", value: number): LessThanOrEqualFilter;

function numericFilter(
  operator: NumericOperator,
  value: number
): GreaterThanOrEqualFilter | LessThanOrEqualFilter;

function numericFilter(
  operator: NumericOperator,
  value: number
): GreaterThanOrEqualFilter | LessThanOrEqualFilter {
  if (operator === "gte") {
    return { operator: "gte", value };
  }

  return { operator: "lte", value };
}

function isNumericBound(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function parseNumericBound(value: unknown): number | undefined {
  const amount = typeof value === "number" ? value : Number(value);

  return Number.isFinite(amount) ? amount : undefined;
}

function isGreaterThanOrEqualFilter(
  filter: FieldFilter
): filter is GreaterThanOrEqualFilter {
  return filter.operator === "gte" && isNumericBound(filter.value);
}

function isLessThanOrEqualFilter(
  filter: FieldFilter
): filter is LessThanOrEqualFilter {
  return filter.operator === "lte" && isNumericBound(filter.value);
}

interface LikeFilter extends BaseFieldFilter {
  operator: "like";
  value: string;
}

function isLikeFilter(filter: FieldFilter): filter is LikeFilter {
  return filter.operator === "like";
}

interface IsNullFilter extends BaseFieldFilter {
  operator: "isnull";
  value: string;
}

function isNullFilter(filter: FieldFilter): filter is IsNullFilter {
  return filter.operator === "isnull";
}

interface IsNotNullFilter extends BaseFieldFilter {
  operator: "isnotnull";
  value: string;
}

interface InFilter extends BaseFieldFilter {
  operator: "in";
  value: string[];
}

function isInFilter(filter: FieldFilter): filter is InFilter {
  return filter.operator === "in";
}

interface NotInFilter extends BaseFieldFilter {
  operator: "nin";
  value: string[];
}

function isNotInFilter(filter: FieldFilter): filter is NotInFilter {
  return filter.operator === "nin";
}

/**
 * Inclusive [from, to] in one filter. Kept out of `comparisonOperators` on purpose — that list is
 * the backend wire vocabulary, and no API accepts a between.
 */
interface BetweenFilter {
  operator: "between";
  value: [number, number];
}

function isBetweenFilter(filter: FieldFilter): filter is BetweenFilter {
  return (
    filter.operator === "between" &&
    Array.isArray(filter.value) &&
    filter.value.length === 2 &&
    filter.value.every(isNumericBound)
  );
}

type FieldFilter =
  | EqualFilter
  | NotEqualFilter
  | GreaterThanFilter
  | GreaterThanOrEqualFilter
  | LessThanFilter
  | LessThanOrEqualFilter
  | LikeFilter
  | IsNullFilter
  | IsNotNullFilter
  | InFilter
  | NotInFilter
  | BetweenFilter;

export type {
  FieldFilter,
  EqualFilter,
  NotEqualFilter,
  IsNullFilter,
  InFilter,
  NotInFilter,
  ComparisonOperators,
  LikeFilter,
  GreaterThanOrEqualFilter,
  LessThanOrEqualFilter,
  NumericOperator,
  BetweenFilter,
};
export {
  isEqualFilter,
  isNotEqualFilter,
  isNullFilter,
  isInFilter,
  isNotInFilter,
  isLikeFilter,
  isGreaterThanOrEqualFilter,
  isLessThanOrEqualFilter,
  isBetweenFilter,
  isNumericBound,
  parseNumericBound,
  numericFilter,
};
